<script setup lang="ts">
import {computed, inject, ref} from "vue";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import DMPButton from "@/core/components/buttons/DMPButton.vue";
import BackingReportCard from "@/backing/components/reports/BackingReportCard.vue";
import InterviewInstanceBacking from "@/interview/components/instance-modal/InterviewInstanceBacking.vue";
import StartJourneyModal
    from "@/appointment/components/consumer-appointment-timeline/start-journey/StartJourneyModal.vue";
import CustomerCertificationDialog from "@/customer/components/customer-certification/CustomerCertificationDialog.vue";
import {AudioLines, CalendarDays, Send, Smartphone} from "@lucide/vue";
import CustomerCommunicationDialog from "@/customer/components/customer-communication/CustomerCommunicationDialog.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import InterviewContainer from "@/interview/components/instance-modal/InterviewContainer.vue";
import InterviewSurveyDialog from "@/interview/components/instance-modal/InterviewSurveyDialog.vue";
import BackingBillingLabel from "@/backing/components/BackingBillingLabel.vue";
import {DotLottieVue} from "@lottiefiles/dotlottie-vue";
import MarkdownView from "@/core/components/markdown/Markdown.vue";
import InterviewDeleteButton from "@/interview/components/interview-delete-button/InterviewDeleteButton.vue";
import AutoEvaluationCard from "@/interview/components/instance-modal/AutoEvaluationCard.vue";
import {useInterviewStore} from "@/stores/interview-store";
import {BackingInterviewStep} from "@/backing/interfaces/backing";
import {usePatientStore} from "@/stores/patient.store";
import {useToastStore} from "@/stores/toast.store";
import {storeToRefs} from "pinia";
import {interviewApi} from "@/container";
import dayjs from "dayjs";
import useInterviewPrefill from "@/interview/composables/useInterviewPrefill";
import InterviewAudioService from "@/interview/services/InterviewAudioService";
import SurveyAISummary from "@/interview/components/instance-modal/SurveyAISummary.vue";
import useSurveySummaryListener from "@/interview/composables/useSurveySummaryListener";
import {Dialog, DialogHeader, DialogScrollContent, DialogTitle, DialogTrigger} from "@/core/components/ui/dialog";
import InterviewGuidelineSurveyReport from "@/interview/components/instance-modal/InterviewGuidelineSurveyReport.vue";
import SurveyMSSanteButton from "@/mssante/components/SurveyMSSanteButton.vue";

const interviewStore = useInterviewStore();
const patientStore = usePatientStore();
const {showSuccess, showError} = useToastStore();

const {surveyInstance, error: prefillError, isReady, isPrefillComplete} = useInterviewPrefill();
useSurveySummaryListener(surveyInstance);

const {audio, interview, interviewInstance, consumerKey, backing, program} = storeToRefs(interviewStore);
const {customer} = storeToRefs(patientStore);

const submitting = ref(false);
const downloadingAudio = ref(false);
const appointmentModalOpen = ref(false);
const certificationModalOpen = ref(false);

/**
 * Check if current interview step is configured as auto-evaluation.
 */
const isAutoEvaluation = computed((): boolean => {
    if (!backing.value?.steps || !interview.value) {
        return false;
    }

    const interviewSteps = backing.value.steps.filter(
        (step): step is BackingInterviewStep => step.resource_type === 'interview'
    );

    const currentStep = interviewSteps.find(
        step => step.resource_id === interview.value?.id
    );

    return currentStep?.auto_evaluation ?? false;
});

/**
 * Get other interview steps from the backing (excluding current interview).
 */
const otherInterviewSteps = computed((): BackingInterviewStep[] => {
    if (!backing.value?.steps || !interview.value) {
        return [];
    }

    return backing.value.steps.filter(
        (step): step is BackingInterviewStep =>
            step.resource_type === 'interview' && step.resource_id !== interview.value?.id
    );
});

/**
 * Submit the survey answers.
 */
const submitSurvey = async (answers: Record<number, (string | number | boolean | undefined)[]>): Promise<void> => {
    if (!interview.value || !interviewInstance.value) {
        return;
    }

    submitting.value = true;

    const formattedAnswers = Object.keys(answers).map(key => ({
        question_id: Number(key),
        answer: answers[key]
    }));

    try {
        const instance = await interviewApi().updateInstance(
            interview.value.id,
            interviewInstance.value.id,
            {
                survey_answers: formattedAnswers,
                ended_at: dayjs().format('YYYY-MM-DD HH:mm:ss')
            }
        );

        interviewInstance.value = instance;
        interviewStore.updatePatientStore(interviewInstance.value);

        if (surveyInstance.value) {
            surveyInstance.value.completed_at = instance.ended_at;
        }

        showSuccess({content: "Le compte-rendu ameli a été enregistré avec succès."});
    } catch {
        showError({content: "Une erreur est survenue lors de l'enregistrement du compte-rendu ameli."});
    } finally {
        submitting.value = false;
    }
};

/**
 * Download the audio file.
 */
const downloadAudio = async (): Promise<void> => {
    if (!audio.value || !interview.value) {
        return;
    }

    const name = interviewStore.consumerName?.replace(/\s/g, '_') ?? '';
    const date = dayjs().format("DD_MM_YYYY");
    const fileName = `${interview.value.name}_${name}_${date}`;

    downloadingAudio.value = true;

    try {
        await InterviewAudioService.downloadAudio(
            interview.value.id,
            audio.value,
            fileName
        );
    } finally {
        downloadingAudio.value = false;
    }
};

/**
 * Refresh customer data after certification.
 */
const refreshCustomer = (): void => {
    patientStore.refreshCustomer();
};

const close = inject('close', () => {
});
</script>

<template>
  <BaseContainer class="pb-20">
    <div v-if="!isReady" class="bg-lightPurple-lightest flex p-6 gap-4 items-center">
      <img
        src="@/assets/images/let-the-magic-happens.png" alt="let-the-magic-append"
        class="h-[120px] w-[120px]"
      >
      <div class="flex-1">
        <h1 class="text-black">
          Laissez la magie opérer !
        </h1>
        <p class="leading-7">
          L'IA prépare le compte-rendu d'entretien, ce sera bientôt prêt.<br>Vous pourrez le finaliser plus tard
        </p>
      </div>
    </div>

    <div v-else class="p-6 bg-lightPurple-lightest space-y-6">
      <div class="flex justify-between gap-6 flex-wrap items-center">
        <div class="flex items-center">
          <DotLottieVue
            src="animations/success.json"
            class="w-[60px] h-[60px]"
            :autoplay="true"
          />
          <h1 class="text-3xl">
            Entretien terminé !
          </h1>
        </div>
        <div class="flex gap-4 items-center">
          <DMPButton />
          <SurveyMSSanteButton :survey-instance-id="surveyInstance?.id" :consumer-key="consumerKey ?? undefined" />
        </div>
      </div>
      <div>
        <div v-if="!prefillError" class="flex flex-col md:flex-row gap-6 flex-wrap">
          <BackingReportCard
            v-if="backing?.patient_report_engine && consumerKey && !isAutoEvaluation"
            :configuration="backing.patient_report_engine"
            :consumer-key="consumerKey"
            :backing="backing"
            :disabled="!isPrefillComplete"
            :last-instance-date="new Date(interviewInstance!.started_at)"
            type="patient"
            class="flex-1"
          />
          <div
            v-if="interview?.is_survey_relevant"
            class="bg-white border-[1px] border-gray-400 flex-1 flex flex-col gap-4 p-4 justify-between"
          >
            <h2 class="text-lg font-semibold">
              Complétez le compte-rendu ameli
            </h2>
            <div class="self-center">
              <InterviewSurveyDialog
                v-if="interviewInstance"
                :instance="surveyInstance"
                :submitting="submitting"
                @save="submitSurvey"
              >
                <BaseButton primary class="px-12" :loading="!surveyInstance">
                  Compte-rendu ameli
                </BaseButton>
              </InterviewSurveyDialog>
            </div>
          </div>
        </div>
        <div v-else>
          <p>
            Une erreur est survenue lors de la pré-remplissage du compte-rendu ameli. Veuillez réessayer plus tard.
          </p>
        </div>
      </div>

      <div v-if="interviewInstance?.ended_at && interview?.post_interview_instructions" class="mt-4">
        <MarkdownView :content="interview.post_interview_instructions" />
      </div>

      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <div v-if="interviewInstance?.guideline_survey_answer_instance_id">
            <Dialog>
              <DialogTrigger>
                <BaseButton>
                  Voir le déroulé
                </BaseButton>
              </DialogTrigger>
              <DialogScrollContent class="lg:max-w-4xl max-h-[90vh] flex flex-col overflow-y-scroll">
                <DialogHeader>
                  <DialogTitle>Déroulé {{ interview!.name }}</DialogTitle>
                </DialogHeader>
                <InterviewGuidelineSurveyReport :guide-survey-instance-id="interviewInstance.guideline_survey_answer_instance_id" />
              </DialogScrollContent>
            </Dialog>
          </div>
          <BaseButton v-if="audio" :loading="downloadingAudio" @click="downloadAudio">
            <AudioLines class="mr-2 size-6" /> Télécharger l'audio
          </BaseButton>
        </div>
        <BackingBillingLabel v-if="backing && !isAutoEvaluation" :backing="backing" />
      </div>
    </div>

    <div class="p-6 flex-1 flex flex-col gap-6">
      <SurveyAISummary v-if="surveyInstance?.ai_summary?.summary" :summary="surveyInstance.ai_summary.summary" />
      <AutoEvaluationCard
        v-if="isAutoEvaluation"
        :survey-instance="surveyInstance"
        @save="submitSurvey"
      />

      <div class="text-center flex-1 space-y-2">
        <h2 v-if="!isPrefillComplete" class="text-black">
          En attendant le compte-rendu...
        </h2>
        <div class="flex gap-3 items-center flex-wrap justify-center">
          <BaseButton density="comfortable" @click="appointmentModalOpen = true">
            Rendez-vous
            <CalendarDays class="w-5 h-5 ml-1" />
          </BaseButton>
          <CustomerCommunicationDialog
            v-if="customer"
            :customer="customer"
            :program-id="program?.id"
            @certified="refreshCustomer"
          >
            <BaseButton density="comfortable">
              SMS
              <Send class="w-5 h-5 ml-1" />
            </BaseButton>
          </CustomerCommunicationDialog>
          <BaseButton
            v-if="customer && !customer.certification?.validated_at" density="comfortable"
            @click="certificationModalOpen = true"
          >
            Connecter {{ customer.information?.name }}
            <Smartphone class="w-5 h-5 ml-1" />
          </BaseButton>
        </div>
      </div>

      <div v-if="interview && otherInterviewSteps?.length">
        <h2 class="text-black">
          Ou continuez avec un autre entretien
        </h2>
        <InterviewContainer>
          <InterviewInstanceBacking :interview-steps="otherInterviewSteps" :interview-id="interview.id" />
        </InterviewContainer>
      </div>

      <div class="flex justify-center flex-wrap gap-4">
        <InterviewDeleteButton
          v-if="interview && interviewInstance"
          :interview="interview"
          :interview-instance="interviewInstance"
          label="Supprimer l'entretien"
        />
        <BaseButton @click="close">
          Retour sur la fiche patient
        </BaseButton>
      </div>
    </div>
    <StartJourneyModal
      v-if="customer"
      :open="appointmentModalOpen"
      :customer="customer"
      @update:open="value => appointmentModalOpen = value"
      @open-certification-dialog="appointmentModalOpen = false; certificationModalOpen = true"
    />
    <CustomerCertificationDialog v-if="customer" v-model:open="certificationModalOpen" :customer="customer" />
  </BaseContainer>
</template>

<style scoped>

</style>
